import { inject, Injectable } from '@angular/core';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { InlineFilterService } from '@common/services/inline-filter-service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { OPTION_ALL, SECTION_FILTER_ID_ASCENDING, SECTION_FILTER_ID_DESCENDING, SECTION_FILTER_ID_TODAY } from '@src/suite/common/enums/constants';
import { GlobalTablesActions } from '@src/suite/common/store/global-tables/global-tables.actions';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { HelloResultsActions } from '@src/suite/common/store/hello-results/hello-results.actions';
import { SORT_BY_FILTER_ID, TODAY_ONLY_FILTER_ID } from '@src/suite/common/utils/section-filter-utils';
import { catchError, EMPTY, filter, from, map, mergeMap } from 'rxjs';

import { selectI18N } from '../i18n/selectors/i18n.selectors';
import { InlineFilterActions } from './inline-filter.actions';
import { enrichedConfigKey, FILTER_SECTION_ALL, FilterObjectType, FilterObjectTypes, SectionFiltersPayload } from './inline-filter.models';
import { selectInlineFilterFeature } from './inline-filter.selectors';

const TODAY_ONLY_OBJECT_TYPES: ReadonlySet<FilterObjectType> = new Set([FilterObjectTypes.ASSIGNMENT, FilterObjectTypes.STORY]);

const DEFAULT_FIELD_IDS_BY_SECTION: ReadonlyMap<FilterObjectType, readonly string[]> = new Map([
    [FilterObjectTypes.ASSIGNMENT, [PSTFieldNames.DescriptionType, PSTFieldNames.PSTAssignmentStatus, PSTFieldNames.PSTDesk, PSTFieldNames.PSTApproved, PSTFieldNames.PSTAssignmentOwner]],
    [FilterObjectTypes.STORY, [PSTFieldNames.PSTStoryOutputType, PSTFieldNames.PSTStoryStatus, PSTFieldNames.PSTDesk, PSTFieldNames.Approved, PSTFieldNames.PSTStoryOwner]],
    [FilterObjectTypes.STORY_TEMPLATE, [PSTFieldNames.PSTDesk, PSTFieldNames.PSTStoryOutputType]],
]);

const buildTodayOnlyFilter = (languageSettings: Record<string, string>): StorytellingFilterDefinition => ({
    ID: TODAY_ONLY_FILTER_ID,
    PSTField: PSTFieldNames.TargetPublicationDate,
    DefaultCaption: languageSettings?.PSTWebSectionFilterTodayOnly,
    Type: 'checkbox',
    DisplayType: 'checkbox',
    Options: [
        { id: OPTION_ALL, title: languageSettings?.PSTWebSectionFilterTodayOnlyAll },
        { id: SECTION_FILTER_ID_TODAY, title: languageSettings?.PSTWebSectionFilterTodayOnlyToday },
    ],
});

const buildSortByFilter = (languageSettings: Record<string, string>): StorytellingFilterDefinition => ({
    ID: SORT_BY_FILTER_ID,
    PSTField: PSTFieldNames.TargetPublicationDate,
    DefaultCaption: languageSettings?.PSTWebSectionFilterSortByTargetDate,
    Type: 'checkbox',
    DisplayType: 'checkbox',
    Options: [
        { id: OPTION_ALL, title: languageSettings?.PSTWebSectionFilterSortByTargetDateNone },
        { id: SECTION_FILTER_ID_ASCENDING, title: languageSettings?.PSTWebSectionFilterSortByTargetDateASC },
        { id: SECTION_FILTER_ID_DESCENDING, title: languageSettings?.PSTWebSectionFilterSortByTargetDateDESC },
    ],
});

@Injectable()
export class InlineFilterEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private service = inject(InlineFilterService);
    private globalTablesService = inject(GlobalTablesService);

    enrichFilterConfigs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GlobalTablesActions.setGlobalFields),
            concatLatestFrom(() => [this.store.select(selectFieldDefsDict), this.store.select(selectI18N)]),
            map(([, fieldDefsDict, languageSettings]) => {
                // Seed every known section so defaults and synthetics still apply when no
                // FieldDef declares it via SectionFilter.
                const fieldsBySection = new Map<FilterObjectType, string[]>();
                for (const objectType of DEFAULT_FIELD_IDS_BY_SECTION.keys()) {
                    fieldsBySection.set(objectType, []);
                }

                for (const id of Object.keys(fieldDefsDict ?? {})) {
                    const sectionFilter = fieldDefsDict[id]?.SectionFilter;
                    if (!sectionFilter?.length) continue;
                    for (const section of sectionFilter) {
                        const trimmed = section?.trim();
                        if (!trimmed) continue;
                        const list = fieldsBySection.get(trimmed) ?? [];
                        list.push(id);
                        fieldsBySection.set(trimmed, list);
                    }
                }

                const enriched: Record<string, StorytellingFilterDefinition[]> = {};
                for (const [objectType, fieldIds] of fieldsBySection) {
                    const ids = fieldIds.length ? fieldIds : (DEFAULT_FIELD_IDS_BY_SECTION.get(objectType) ?? []);
                    const list = this.globalTablesService.buildFiltersForFieldIds(ids);
                    if (TODAY_ONLY_OBJECT_TYPES.has(objectType)) {
                        list.push(buildTodayOnlyFilter(languageSettings));
                        list.push(buildSortByFilter(languageSettings));
                    }
                    enriched[enrichedConfigKey(objectType, FILTER_SECTION_ALL)] = list;
                }
                return InlineFilterActions.setEnrichedConfigs({ enriched });
            })
        );
    });

    hydrateFromHello$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HelloResultsActions.processHelloResults),
            map(({ helloResults }) => {
                const raw = helloResults?.SectionFilters as unknown;
                let states: SectionFiltersPayload = {};
                if (typeof raw === 'string') {
                    if (raw.length) {
                        try {
                            states = JSON.parse(raw) as SectionFiltersPayload;
                        } catch (err) {
                            console.error('[InlineFilter] failed to parse SectionFilters from HelloResults', err);
                        }
                    }
                } else if (raw && typeof raw === 'object') {
                    states = raw as SectionFiltersPayload;
                }
                return InlineFilterActions.setAllFilterStates({ states });
            })
        );
    });

    saveFilterState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InlineFilterActions.saveFilterState),
            concatLatestFrom(() => this.store.select(selectInlineFilterFeature)),
            mergeMap(([{ objectType, section }, slice]) => {
                const snapshot = slice?.workingStates?.[objectType]?.[section] ?? {};
                return from(this.service.saveFilterState(objectType, section, snapshot)).pipe(
                    map(response => (response?.status ? InlineFilterActions.saveFilterStateSuccess({ objectType, section, state: snapshot }) : null)),
                    filter((action): action is ReturnType<typeof InlineFilterActions.saveFilterStateSuccess> => !!action),
                    catchError(err => {
                        console.error(`[InlineFilter] saveFilterState failed for ${objectType}:${section}`, err);
                        return EMPTY;
                    })
                );
            })
        );
    });
}
